import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaBriefcase, FaUsers, FaClipboardList, FaPlus, FaCheckCircle } from 'react-icons/fa';
import apiClient from '../api/api';
import { useAuth } from '../context/AuthContext';
import { StatCard, SectionHeader, ActivityListItem } from './DashboardComponents';

function DashboardHome() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({
    activeJobs: 0,
    totalHires: 0,
    pendingApplications: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // Fetch my jobs
        const jobsResponse = await apiClient.get('/jobs/my-jobs');
        const myJobs = jobsResponse.data || [];
        setJobs(myJobs);

        // Calculate stats
        let activeCount = 0;
        let hiresCount = 0;
        let pendingCount = 0;

        myJobs.forEach(job => {
            // Count active jobs (exclude completed)
            if (job.status === 'open' || job.status === 'running' || job.status === 'bidding') activeCount++;
            
            // Count hires
            if (job.direct_hires) {
                hiresCount += job.direct_hires.filter(h => ['active', 'completed'].includes(h.status)).length;
            }

            // Count pending apps/bids
            if (job.post_model === 'bidding') {
                pendingCount += job.applicants?.length || 0;
            } else {
                pendingCount += job.applicants?.length || 0;
            }
        });

        setStats({
          activeJobs: activeCount,
          totalHires: hiresCount,
          pendingApplications: pendingCount
        });

      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  // Helper to determine status color
  const getStatusColor = (status) => {
      switch(status) {
          case 'open': return 'bg-green-100 text-green-700';
          case 'running': return 'bg-blue-100 text-blue-700';
          case 'completed': return 'bg-gray-100 text-gray-700';
          default: return 'bg-yellow-100 text-yellow-700';
      }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-10 font-sans">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between">
            <div>
                <h1 className="text-3xl font-black text-slate-800 tracking-tight">Buyer Dashboard</h1>
                <p className="text-slate-500 mt-1">Manage your job postings and hires.</p>
            </div>
            <div className="mt-4 md:mt-0">
                <Link to="/publish-job" className="bg-emerald-600 text-white px-5 py-3 rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 flex items-center">
                    <FaPlus className="mr-2" /> Post a Job
                </Link>
            </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            <StatCard 
                title="Active Jobs" 
                value={loading ? '...' : stats.activeJobs} 
                icon={FaBriefcase} 
                color="blue" 
            />
            <StatCard 
                title="Total Hires" 
                value={loading ? '...' : stats.totalHires} 
                icon={FaUsers} 
                color="green" 
            />
            <StatCard 
                title="Pending Apps" 
                value={loading ? '...' : stats.pendingApplications} 
                icon={FaClipboardList} 
                color="yellow" 
            />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Main Content: Recent Jobs */}
            <div className="lg:col-span-2 bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                <SectionHeader 
                    title="Recent Job Posts" 
                    linkText="View All" 
                    linkTo="/my-jobs"
                />
                
                {loading ? (
                    <div className="space-y-4 animate-pulse">
                        {[1,2,3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl"></div>)}
                    </div>
                ) : jobs.length > 0 ? (
                    <div className="space-y-4">
                        {jobs.slice(0, 5).map(job => (
                            <ActivityListItem 
                                key={job.id}
                                title={job.title}
                                subtitle={`Budget: ৳${job.details?.[0]?.budget_per_worker || 'N/A'}`}
                                date={new Date(job.createdAt).toLocaleDateString()}
                                status={job.status.toUpperCase()}
                                statusColor={getStatusColor(job.status)}
                                action={
                                    <Link to={`/job/${job.id}`} className="text-slate-400 hover:text-emerald-600 transition-colors">
                                        View
                                    </Link>
                                }
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        <FaClipboardList className="text-slate-300 text-4xl mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">You haven't posted any jobs yet.</p>
                        <Link to="/publish-job" className="text-emerald-600 font-bold text-sm mt-2 inline-block hover:underline">Create your first job</Link>
                    </div>
                )}
            </div>

            {/* Sidebar: Quick Info */}
            <div className="lg:col-span-1 space-y-8">
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Tips for Buyers</h3>
                    <ul className="space-y-4">
                        <li className="flex items-start">
                            <FaCheckCircle className="text-emerald-500 mt-1 mr-3 flex-shrink-0" />
                            <p className="text-sm text-slate-600">Verify worker profiles before hiring to ensure quality.</p>
                        </li>
                        <li className="flex items-start">
                            <FaCheckCircle className="text-emerald-500 mt-1 mr-3 flex-shrink-0" />
                            <p className="text-sm text-slate-600">Use the chat feature to discuss requirements clearly.</p>
                        </li>
                        <li className="flex items-start">
                            <FaCheckCircle className="text-emerald-500 mt-1 mr-3 flex-shrink-0" />
                            <p className="text-sm text-slate-600">Release payments promptly upon job completion.</p>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardHome;